// src/pages/SuperAdmin/StoreManagement.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Container,
  Card,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Row,
  Col,
  Spinner,
  Tabs,
  Tab,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FiSearch, FiX, FiPackage, FiTag, FiClipboard } from "react-icons/fi";
import axios from "../../api/axios";

// Composant Sélecteur d'Emoji
const EmojiPickerInput = ({ value, onChange, label }) => {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);

  const emojiCategories = [
    {
      name: "Technologie",
      emojis: [
        "📱",
        "💻",
        "🖥️",
        "⌚",
        "📷",
        "🎧",
        "🔊",
        "🎮",
        "🔌",
        "💾",
        "🖨️",
        "📡",
      ],
    },
    {
      name: "Mode & Accessoires",
      emojis: [
        "👕",
        "👟",
        "👗",
        "💍",
        "👜",
        "💄",
        "🧴",
        "👓",
        "🧢",
        "👔",
        "👖",
        "🧦",
      ],
    },
    {
      name: "Maison & Jardin",
      emojis: [
        "🏠",
        "🪑",
        "🔧",
        "💡",
        "🚪",
        "🪟",
        "🌿",
        "🌸",
        "🍃",
        "🧹",
        "🧺",
        "🚿",
      ],
    },
    {
      name: "Sports & Loisirs",
      emojis: [
        "⚽",
        "🏀",
        "🏈",
        "🎾",
        "🏃",
        "🚴",
        "🏋️",
        "🎯",
        "🎳",
        "🏊",
        "🧘",
        "🤸",
      ],
    },
    {
      name: "Alimentation",
      emojis: [
        "🍎",
        "🍕",
        "🍔",
        "🥗",
        "☕",
        "🍷",
        "🥤",
        "🍰",
        "🍦",
        "🍩",
        "🍪",
        "🍫",
      ],
    },
    {
      name: "Santé & Bien-être",
      emojis: [
        "💊",
        "💉",
        "🩺",
        "🧴",
        "🧼",
        "🧽",
        "🩹",
        "💪",
        "🧠",
        "🦷",
        "👁️",
        "👂",
      ],
    },
    {
      name: "Transport",
      emojis: [
        "🚗",
        "✈️",
        "🚢",
        "🚲",
        "🚂",
        "🚁",
        "🛵",
        "🚀",
        "🚤",
        "🛴",
        "🚎",
        "🚕",
      ],
    },
    {
      name: "Divers",
      emojis: [
        "🎁",
        "⭐",
        "❤️",
        "🔥",
        "👍",
        "🎉",
        "💎",
        "🔑",
        "📚",
        "✏️",
        "📎",
        "🔒",
      ],
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <div className="d-flex gap-2">
        <Form.Control
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="📱"
          maxLength={2}
          style={{ width: "80px", textAlign: "center", fontSize: "28px" }}
        />
        <Button
          variant="outline-secondary"
          onClick={() => setShowPicker(!showPicker)}
          type="button"
          className="d-flex align-items-center gap-2"
        >
          <span>😊</span> Choisir un emoji
        </Button>
      </div>
      {showPicker && (
        <div
          ref={pickerRef}
          className="emoji-picker mt-2 p-3 border rounded bg-white shadow"
          style={{
            position: "absolute",
            zIndex: 1060,
            width: "320px",
            maxHeight: "300px",
            overflowY: "auto",
            backgroundColor: "white",
          }}
        >
          {emojiCategories.map((category, catIdx) => (
            <div key={catIdx} className="mb-3">
              <div className="fw-bold small text-muted mb-2">
                {category.name}
              </div>
              <div className="d-flex flex-wrap gap-1">
                {category.emojis.map((emoji) => (
                  <Button
                    key={emoji}
                    variant="light"
                    size="sm"
                    onClick={() => {
                      onChange(emoji);
                      setShowPicker(false);
                    }}
                    className="emoji-btn"
                    style={{
                      fontSize: "24px",
                      padding: "5px 8px",
                      minWidth: "45px",
                    }}
                  >
                    {emoji}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <Form.Text className="text-muted">
        Choisissez un emoji dans la liste ou copiez-collez-en un
      </Form.Text>
    </Form.Group>
  );
};

const StoreManagement = () => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);

  // ✅ États de recherche
  const [productSearch, setProductSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [orderSearch, setOrderSearch] = useState("");

  // ✅ États de filtre
  const [productCategoryFilter, setProductCategoryFilter] = useState("all");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");

  // États pour les produits
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    price_fcfa: "",
    category: "",
    is_available: true,
    is_featured: false,
  });

  // États pour les catégories
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    name_en: "",
    icon: "",
    description: "",
    order: 0,
    is_active: true,
  });
  const [categoryLoading, setCategoryLoading] = useState(false);

  // États pour les commandes
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const productsRes = await axios.get("/store/products/?all=true");
      let productsData = [];
      if (productsRes.data && productsRes.data.results) {
        productsData = productsRes.data.results;
      } else if (Array.isArray(productsRes.data)) {
        productsData = productsRes.data;
      }
      setProducts(productsData);

      const categoriesRes = await axios.get("/store/categories/");
      let categoriesData = [];
      if (categoriesRes.data && categoriesRes.data.results) {
        categoriesData = categoriesRes.data.results;
      } else if (Array.isArray(categoriesRes.data)) {
        categoriesData = categoriesRes.data;
      }
      setCategories(categoriesData);

      const ordersRes = await axios.get("/store/orders/admin/all/");
      let ordersData = [];
      if (ordersRes.data && ordersRes.data.results) {
        ordersData = ordersRes.data.results;
      } else if (Array.isArray(ordersRes.data)) {
        ordersData = ordersRes.data;
      }
      setOrders(ordersData);
    } catch (err) {
      console.error("Erreur chargement:", err);
      toast.error("Erreur chargement des données");
      setProducts([]);
      setCategories([]);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `https://chineafriquecargo.com${imagePath}`;
  };

  // ✅ FILTRES PRODUITS
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesSearch =
        !productSearch ||
        p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
        p.description?.toLowerCase().includes(productSearch.toLowerCase()) ||
        p.category_name?.toLowerCase().includes(productSearch.toLowerCase());

      const matchesCategory =
        productCategoryFilter === "all" ||
        p.category == productCategoryFilter ||
        p.category?.id == productCategoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [products, productSearch, productCategoryFilter]);

  // ✅ FILTRES CATÉGORIES
  const filteredCategories = useMemo(() => {
    return categories.filter((c) => {
      return (
        !categorySearch ||
        c.name?.toLowerCase().includes(categorySearch.toLowerCase()) ||
        c.name_en?.toLowerCase().includes(categorySearch.toLowerCase()) ||
        c.description?.toLowerCase().includes(categorySearch.toLowerCase())
      );
    });
  }, [categories, categorySearch]);

  // ✅ FILTRES COMMANDES
  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const matchesSearch =
        !orderSearch ||
        o.order_number?.toLowerCase().includes(orderSearch.toLowerCase()) ||
        o.client_email?.toLowerCase().includes(orderSearch.toLowerCase()) ||
        o.shipping_name?.toLowerCase().includes(orderSearch.toLowerCase()) ||
        o.shipping_phone?.toLowerCase().includes(orderSearch.toLowerCase());

      const matchesStatus =
        orderStatusFilter === "all" || o.status === orderStatusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [orders, orderSearch, orderStatusFilter]);

  // GESTION DES PRODUITS
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (selectedImages.length + files.length > 10) {
      toast.error("Maximum 10 images par produit");
      return;
    }

    const newImages = [...selectedImages];
    const newPreviews = [...imagePreviews];

    files.forEach((file) => {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name} dépasse 2 Mo`);
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} n'est pas une image`);
        return;
      }
      newImages.push(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        setImagePreviews([...newPreviews]);
      };
      reader.readAsDataURL(file);
    });

    setSelectedImages(newImages);
  };

  const removeImage = (index) => {
    const newImages = [...selectedImages];
    const newPreviews = [...imagePreviews];
    newImages.splice(index, 1);
    newPreviews.splice(index, 1);
    setSelectedImages(newImages);
    setImagePreviews(newPreviews);
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("name", productForm.name || "");
      formData.append("description", productForm.description || "");
      formData.append(
        "price_fcfa",
        Math.round(parseFloat(productForm.price_fcfa) || 0),
      );
      formData.append("category", productForm.category || "");
      formData.append(
        "is_available",
        productForm.is_available ? "true" : "false",
      );
      formData.append(
        "is_featured",
        productForm.is_featured ? "true" : "false",
      );

      if (selectedImages.length > 0) {
        formData.append("main_image", selectedImages[0]);
      }

      for (let i = 1; i < selectedImages.length; i++) {
        formData.append(`additional_image_${i}`, selectedImages[i]);
      }

      if (editingProduct) {
        await axios.put(`/store/products/${editingProduct.id}/`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Produit modifié avec succès");
      } else {
        await axios.post("/store/products/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Produit créé avec succès");
      }

      setShowProductModal(false);
      setEditingProduct(null);
      setSelectedImages([]);
      setImagePreviews([]);
      setProductForm({
        name: "",
        description: "",
        price_fcfa: "",
        category: "",
        is_available: true,
        is_featured: false,
      });
      fetchData();
    } catch (err) {
      console.error("Erreur:", err);
      if (err.response?.data) {
        const errors = err.response.data;
        const errorMessages = [];
        if (errors.price_fcfa)
          errorMessages.push(`Prix: ${errors.price_fcfa.join(", ")}`);
        if (errors.additional_images)
          errorMessages.push(`Images: ${errors.additional_images.join(", ")}`);
        if (errorMessages.length > 0) {
          toast.error(errorMessages.join(" | "));
        } else {
          toast.error("Erreur lors de l'enregistrement");
        }
      } else {
        toast.error("Erreur de connexion");
      }
    } finally {
      setUploading(false);
    }
  };

  const deleteProduct = async (id) => {
    if (window.confirm("Supprimer ce produit ?")) {
      try {
        await axios.delete(`/store/products/${id}/`);
        toast.success("Produit supprimé");
        fetchData();
      } catch (err) {
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || "",
      description: product.description || "",
      price_fcfa: product.price_fcfa || "",
      category: product.category || "",
      is_available: product.is_available === true,
      is_featured: product.is_featured === true,
    });
    setSelectedImages([]);
    setImagePreviews([]);
    setShowProductModal(true);
  };

  // GESTION DES CATÉGORIES
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setCategoryLoading(true);

    try {
      if (editingCategory) {
        await axios.put(
          `/store/categories/${editingCategory.id}/`,
          categoryForm,
        );
        toast.success("Catégorie modifiée avec succès");
      } else {
        await axios.post("/store/categories/", categoryForm);
        toast.success("Catégorie créée avec succès");
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({
        name: "",
        name_en: "",
        icon: "",
        description: "",
        order: 0,
        is_active: true,
      });
      fetchData();
    } catch (err) {
      console.error("Erreur:", err);
      toast.error(
        err.response?.data?.message || "Erreur lors de l'enregistrement",
      );
    } finally {
      setCategoryLoading(false);
    }
  };

  const deleteCategory = async (id) => {
    const productsInCategory = products.filter((p) => p.category === id);
    if (productsInCategory.length > 0) {
      toast.error(
        `Impossible de supprimer cette catégorie (${productsInCategory.length} produit(s) lié(s))`,
      );
      return;
    }

    if (window.confirm("Supprimer cette catégorie ?")) {
      try {
        await axios.delete(`/store/categories/${id}/`);
        toast.success("Catégorie supprimée");
        fetchData();
      } catch (err) {
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  const openCategoryEditModal = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || "",
      name_en: category.name_en || "",
      icon: category.icon || "",
      description: category.description || "",
      order: category.order || 0,
      is_active: category.is_active === true,
    });
    setShowCategoryModal(true);
  };

  // GESTION DES COMMANDES
  const validateOrder = async (orderId, action) => {
    try {
      await axios.post(`/store/orders/${orderId}/validate/`, { action });
      toast.success(
        action === "validate" ? "Commande validée" : "Commande refusée",
      );
      fetchData();
      setShowOrderModal(false);
    } catch (err) {
      toast.error("Erreur lors de l'opération");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { text: "🟡 En attente", variant: "warning" },
      validated: { text: "🟢 Validée", variant: "success" },
      refused: { text: "🔴 Refusée", variant: "danger" },
      shipped: { text: "📦 Expédiée", variant: "info" },
      delivered: { text: "✅ Livrée", variant: "secondary" },
    };
    return config[status] || { text: status, variant: "secondary" };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("fr-FR");
    } catch {
      return "-";
    }
  };

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <Spinner animation="border" style={{ color: "#C62828" }} />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold" style={{ color: "#C62828" }}>
          🛍️ Gestion du Store
        </h2>
        <Button variant="outline-secondary" as={Link} to="/superadmin">
          ← Retour au Dashboard
        </Button>
      </div>

      <Tabs defaultActiveKey="products" className="mb-4">
        {/* Onglet Produits */}
        <Tab
          eventKey="products"
          title={
            <>
              <FiPackage className="me-1" /> Produits ({filteredProducts.length}
              )
            </>
          }
        >
          <Card className="border-0 shadow-lg rounded-4">
            <Card.Header className="bg-white border-0 pt-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h4 className="fw-bold mb-0">
                  📦 Produits ({filteredProducts.length})
                </h4>
                <div className="d-flex gap-2">
                  {/* ✅ Recherche produits */}
                  <InputGroup style={{ width: "280px" }}>
                    <InputGroup.Text>
                      <FiSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Rechercher un produit..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                    />
                    {productSearch && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setProductSearch("")}
                      >
                        <FiX />
                      </Button>
                    )}
                  </InputGroup>
                  {/* ✅ Filtre catégorie */}
                  <Form.Select
                    style={{ width: "200px" }}
                    value={productCategoryFilter}
                    onChange={(e) => setProductCategoryFilter(e.target.value)}
                  >
                    <option value="all">Toutes les catégories</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.icon} {c.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setEditingProduct(null);
                      setProductForm({
                        name: "",
                        description: "",
                        price_fcfa: "",
                        category: "",
                        is_available: true,
                        is_featured: false,
                      });
                      setSelectedImages([]);
                      setImagePreviews([]);
                      setShowProductModal(true);
                    }}
                  >
                    + Nouveau produit
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {filteredProducts.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  {productSearch
                    ? "Aucun produit trouvé pour cette recherche"
                    : "Aucun produit pour le moment"}
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover>
                    <thead className="bg-light">
                      <tr>
                        <th style={{ width: "80px" }}>Image</th>
                        <th>Nom</th>
                        <th>Catégorie</th>
                        <th>Prix (FCFA)</th>
                        <th>Disponible</th>
                        <th>En vedette</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((p) => (
                        <tr key={p.id}>
                          <td className="text-center">
                            {p.main_image ? (
                              <img
                                src={getImageUrl(p.main_image)}
                                alt={p.name}
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                }}
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/50?text=No+Image";
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  backgroundColor: "#f0f0f0",
                                  borderRadius: "8px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                📦
                              </div>
                            )}
                          </td>
                          <td>
                            <strong>{p.name}</strong>
                            {p.is_featured && (
                              <Badge bg="warning" className="ms-2">
                                ⭐
                              </Badge>
                            )}
                          </td>
                          <td>{p.category_name || "-"}</td>
                          <td>{p.price_fcfa?.toLocaleString()} FCFA</td>
                          <td>
                            {p.is_available ? (
                              <Badge bg="success">Oui</Badge>
                            ) : (
                              <Badge bg="secondary">Non</Badge>
                            )}
                          </td>
                          <td>{p.is_featured ? "⭐ Oui" : "-"}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-2"
                              onClick={() => openEditModal(p)}
                            >
                              Modifier
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => deleteProduct(p.id)}
                            >
                              Supprimer
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Onglet Catégories */}
        <Tab
          eventKey="categories"
          title={
            <>
              <FiTag className="me-1" /> Catégories ({filteredCategories.length}
              )
            </>
          }
        >
          <Card className="border-0 shadow-lg rounded-4">
            <Card.Header className="bg-white border-0 pt-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h4 className="fw-bold mb-0">
                  🏷️ Catégories ({filteredCategories.length})
                </h4>
                <div className="d-flex gap-2">
                  {/* ✅ Recherche catégories */}
                  <InputGroup style={{ width: "280px" }}>
                    <InputGroup.Text>
                      <FiSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Rechercher une catégorie..."
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                    />
                    {categorySearch && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setCategorySearch("")}
                      >
                        <FiX />
                      </Button>
                    )}
                  </InputGroup>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setEditingCategory(null);
                      setCategoryForm({
                        name: "",
                        name_en: "",
                        icon: "",
                        description: "",
                        order: 0,
                        is_active: true,
                      });
                      setShowCategoryModal(true);
                    }}
                  >
                    + Nouvelle catégorie
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {filteredCategories.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  {categorySearch
                    ? "Aucune catégorie trouvée pour cette recherche"
                    : "Aucune catégorie pour le moment"}
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover>
                    <thead className="bg-light">
                      <tr>
                        <th>Icône</th>
                        <th>Nom (FR)</th>
                        <th>Nom (EN)</th>
                        <th>Ordre</th>
                        <th>Statut</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCategories.map((c) => (
                        <tr key={c.id}>
                          <td className="text-center fs-4">{c.icon || "🏷️"}</td>
                          <td>
                            <strong>{c.name}</strong>
                          </td>
                          <td>{c.name_en || "-"}</td>
                          <td>{c.order || 0}</td>
                          <td>
                            {c.is_active ? (
                              <Badge bg="success">Active</Badge>
                            ) : (
                              <Badge bg="secondary">Inactive</Badge>
                            )}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-2"
                              onClick={() => openCategoryEditModal(c)}
                            >
                              Modifier
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => deleteCategory(c.id)}
                            >
                              Supprimer
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Onglet Commandes */}
        <Tab
          eventKey="orders"
          title={
            <>
              <FiClipboard className="me-1" /> Commandes (
              {filteredOrders.length})
            </>
          }
        >
          <Card className="border-0 shadow-lg rounded-4">
            <Card.Header className="bg-white border-0 pt-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h4 className="fw-bold mb-0">
                  📋 Commandes ({filteredOrders.length})
                </h4>
                <div className="d-flex gap-2">
                  {/* ✅ Recherche commandes */}
                  <InputGroup style={{ width: "280px" }}>
                    <InputGroup.Text>
                      <FiSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="N° commande, client, tel..."
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                    />
                    {orderSearch && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setOrderSearch("")}
                      >
                        <FiX />
                      </Button>
                    )}
                  </InputGroup>
                  {/* ✅ Filtre statut */}
                  <Form.Select
                    style={{ width: "180px" }}
                    value={orderStatusFilter}
                    onChange={(e) => setOrderStatusFilter(e.target.value)}
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="pending">🟡 En attente</option>
                    <option value="validated">🟢 Validée</option>
                    <option value="refused">🔴 Refusée</option>
                    <option value="shipped">📦 Expédiée</option>
                    <option value="delivered">✅ Livrée</option>
                  </Form.Select>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {filteredOrders.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  {orderSearch
                    ? "Aucune commande trouvée pour cette recherche"
                    : "Aucune commande pour le moment"}
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover>
                    <thead className="bg-light">
                      <tr>
                        <th>N° Commande</th>
                        <th>Client</th>
                        <th>Montant</th>
                        <th>Statut</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((o) => {
                        const status = getStatusBadge(o.status);
                        return (
                          <tr key={o.id}>
                            <td className="font-monospace">{o.order_number}</td>
                            <td>
                              {o.client_email}
                              <br />
                              <small className="text-muted">
                                {o.shipping_phone}
                              </small>
                            </td>
                            <td>{o.total_amount?.toLocaleString()} FCFA</td>
                            <td>
                              <Badge bg={status.variant}>{status.text}</Badge>
                            </td>
                            <td>{formatDate(o.created_at)}</td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => {
                                  setSelectedOrder(o);
                                  setShowOrderModal(true);
                                }}
                              >
                                Détails
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* MODAL PRODUIT */}
      <Modal
        show={showProductModal}
        onHide={() => setShowProductModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingProduct ? "Modifier le produit" : "Nouveau produit"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleProductSubmit}>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom du produit *</Form.Label>
                  <Form.Control
                    type="text"
                    value={productForm.name}
                    onChange={(e) =>
                      setProductForm({ ...productForm, name: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Prix (FCFA) *</Form.Label>
                  <Form.Control
                    type="number"
                    value={productForm.price_fcfa}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        price_fcfa: e.target.value,
                      })
                    }
                    required
                    step="1"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={productForm.description}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    description: e.target.value,
                  })
                }
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Catégorie</Form.Label>
                  <Form.Select
                    value={productForm.category || ""}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        category: e.target.value,
                      })
                    }
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories
                      .filter((c) => c.is_active)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.icon} {c.name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Images (max 10)</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                  />
                  <Form.Text className="text-muted">
                    La première image sera l'image principale
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {imagePreviews.length > 0 && (
              <div className="mb-3">
                <Form.Label>
                  Aperçu des images ({imagePreviews.length}/10)
                </Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {imagePreviews.map((preview, idx) => (
                    <div key={idx} className="position-relative">
                      <img
                        src={preview}
                        alt={`Aperçu ${idx + 1}`}
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid #ddd",
                        }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 rounded-circle p-0"
                        style={{
                          width: "20px",
                          height: "20px",
                          fontSize: "10px",
                        }}
                        onClick={() => removeImage(idx)}
                      >
                        ×
                      </Button>
                      {idx === 0 && (
                        <Badge
                          bg="primary"
                          className="position-absolute bottom-0 start-0 m-1"
                          style={{ fontSize: "10px" }}
                        >
                          Principale
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Disponible à la vente"
                    checked={productForm.is_available}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        is_available: e.target.checked,
                      })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Mettre en vedette (⭐)"
                    checked={productForm.is_featured}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        is_featured: e.target.checked,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowProductModal(false)}
            >
              Annuler
            </Button>
            <Button type="submit" variant="danger" disabled={uploading}>
              {uploading ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL CATÉGORIE AVEC SÉLECTEUR D'EMOJI */}
      <Modal
        show={showCategoryModal}
        onHide={() => setShowCategoryModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCategory ? "Modifier la catégorie" : "Nouvelle catégorie"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCategorySubmit}>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom (Français) *</Form.Label>
                  <Form.Control
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) =>
                      setCategoryForm({ ...categoryForm, name: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <EmojiPickerInput
                  value={categoryForm.icon}
                  onChange={(val) =>
                    setCategoryForm({ ...categoryForm, icon: val })
                  }
                  label="Icône"
                />
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Nom (Anglais)</Form.Label>
              <Form.Control
                type="text"
                value={categoryForm.name_en}
                onChange={(e) =>
                  setCategoryForm({ ...categoryForm, name_en: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={categoryForm.description}
                onChange={(e) =>
                  setCategoryForm({
                    ...categoryForm,
                    description: e.target.value,
                  })
                }
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ordre d'affichage</Form.Label>
                  <Form.Control
                    type="number"
                    value={categoryForm.order}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        order: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Catégorie active"
                    checked={categoryForm.is_active}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        is_active: e.target.checked,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Alert variant="info" className="mt-2">
              <small>
                💡 <strong>Astuce :</strong> Cliquez sur "Choisir un emoji" pour
                sélectionner une icône
              </small>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowCategoryModal(false)}
            >
              Annuler
            </Button>
            <Button type="submit" variant="danger" disabled={categoryLoading}>
              {categoryLoading ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL COMMANDE */}
      <Modal
        show={showOrderModal}
        onHide={() => setShowOrderModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Commande {selectedOrder?.order_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <>
              <div className="mb-3">
                <strong>Client :</strong> {selectedOrder.client_email}
              </div>
              <div className="mb-3">
                <strong>Téléphone :</strong> {selectedOrder.shipping_phone}
              </div>
              <div className="mb-3">
                <strong>Adresse :</strong> {selectedOrder.shipping_address}
              </div>
              <div className="mb-3">
                <strong>Montant :</strong>{" "}
                {selectedOrder.total_amount?.toLocaleString()} FCFA
              </div>
              <div className="mb-3">
                <strong>Statut :</strong>{" "}
                <Badge bg={getStatusBadge(selectedOrder.status).variant}>
                  {getStatusBadge(selectedOrder.status).text}
                </Badge>
              </div>
              <hr />
              <h6>Produits commandés :</h6>
              <ul>
                {selectedOrder.items?.map((item, idx) => (
                  <li key={idx}>
                    {item.name} x {item.quantity} -{" "}
                    {item.price?.toLocaleString()} FCFA
                  </li>
                ))}
              </ul>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedOrder?.status === "pending" && (
            <>
              <Button
                variant="success"
                onClick={() => validateOrder(selectedOrder.id, "validate")}
              >
                ✅ Valider la commande
              </Button>
              <Button
                variant="danger"
                onClick={() => validateOrder(selectedOrder.id, "refuse")}
              >
                ❌ Refuser la commande
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={() => setShowOrderModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
                .emoji-picker {
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    border-radius: 8px;
                }
                .emoji-btn:hover {
                    background-color: #f0f0f0;
                    transform: scale(1.05);
                }
            `}</style>
    </Container>
  );
};

export default StoreManagement;
